import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiCreate, apiList } from '../../api/apiClient';
import { refreshAllCounters } from '../../utils/refreshCounters';

type SetupStep = 'method' | 'discover' | 'manual' | 'nvr' | 'bridge' | 'done';
type CameraMethod = 'discover' | 'manual' | 'nvr' | 'bridge';

interface DiscoveredCamera {
  ip: string; name: string; manufacturer: string; model: string; rtsp_url?: string; selected?: boolean;
}

interface Props {
  open: boolean; onClose: () => void; properties: any[]; onCameraAdded: () => void;
  onTuringChosen?: () => void;
}

export const CameraSetupWizard: React.FC<Props> = ({ open, onClose, properties, onCameraAdded, onTuringChosen }) => {
  const [step, setStep] = useState<SetupStep>('method');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredCameras, setDiscoveredCameras] = useState<DiscoveredCamera[]>([]);
  const [manualForm, setManualForm] = useState({ name: '', rtsp_url: '', username: '', password: '' });
  const [nvrForm, setNvrForm] = useState({ host: '', port: '80', username: 'admin', password: '', type: 'hikvision' });
  const [nvrCameras, setNvrCameras] = useState<any[]>([]);
  const [nvrLoading, setNvrLoading] = useState(false);
  const [bridgeForm, setBridgeForm] = useState({ device_id: '', name: '' });
  const [bridgeDevices, setBridgeDevices] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      setStep('method');
      setDiscoveredCameras([]);
      setManualForm({ name: '', rtsp_url: '', username: '', password: '' });
      setNvrCameras([]);
      if (properties.length > 0 && !selectedPropertyId) setSelectedPropertyId(properties[0].uuid || String(properties[0].id));
    }
  }, [open]);

  useEffect(() => {
    if (step === 'bridge') {
      apiList('bridge_devices').then(d => {
        setBridgeDevices((d || []).filter((b: any) => (b.device_type || b.type || '').toLowerCase().includes('cam')));
      }).catch(() => setBridgeDevices([]));
    }
  }, [step]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const bridges = await apiList('bridge_devices').catch(() => []);
      const online = bridges.find((b: any) => b.status === 'online');
      if (online?.local_ip) {
        try {
          const res = await fetch(`http://${online.local_ip}:3000/cameras/discover`, { signal: AbortSignal.timeout(10000) });
          const data = await res.json();
          setDiscoveredCameras((data.cameras || []).map((c: any) => ({ ...c, selected: true })));
        } catch { setDiscoveredCameras([]); }
      }
    } catch {} finally { setDiscovering(false); }
  };

  const handleNvrConnect = async () => {
    setNvrLoading(true);
    try {
      const bridges = await apiList('bridge_devices').catch(() => []);
      const bridge = bridges.find((b: any) => b.status === 'online');
      if (bridge?.local_ip) {
        const res = await fetch(`http://${bridge.local_ip}:3000/cameras/nvr`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nvrForm), signal: AbortSignal.timeout(15000),
        });
        const data = await res.json();
        setNvrCameras(data.cameras || []);
      }
    } catch {} finally { setNvrLoading(false); }
  };

  const addCamera = async (camera: { name: string; rtsp_url: string; source: string; ip?: string; manufacturer?: string; model?: string }) => {
    setSaving(true);
    try {
      await apiCreate('devices', {
        name: camera.name, device_name: camera.name, device_type: 'camera', type: 'camera',
        property_id: selectedPropertyId, connection_type: camera.source, rtsp_url: camera.rtsp_url,
        ip_address: camera.ip || '', manufacturer: camera.manufacturer || '', model: camera.model || '',
        connection_status: 'online', status: 'online',
      });
      refreshAllCounters();
      setStep('done');
      onCameraAdded();
    } catch { alert('Failed to add camera.'); } finally { setSaving(false); }
  };

  const addMultiple = async () => {
    setSaving(true);
    try {
      for (const cam of discoveredCameras.filter(c => c.selected)) {
        await apiCreate('devices', {
          name: cam.name, device_name: cam.name, device_type: 'camera', type: 'camera',
          property_id: selectedPropertyId, connection_type: 'onvif', rtsp_url: cam.rtsp_url || '',
          ip_address: cam.ip, manufacturer: cam.manufacturer, model: cam.model,
          connection_status: 'online', status: 'online',
        });
      }
      refreshAllCounters();
      setStep('done');
      onCameraAdded();
    } catch {} finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Camera" size="lg">
      {step !== 'done' && (
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          {step !== 'method' && (
            <button onClick={() => setStep('method')} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back
            </button>
          )}
          <Badge color="blue">{step === 'method' ? 'Choose Method' : step === 'discover' ? 'Auto-Discover' : step === 'manual' ? 'Manual RTSP' : step === 'nvr' ? 'Connect NVR' : 'Bridge Camera'}</Badge>
          {selectedPropertyId && <span className="text-xs text-gray-400 ml-auto">Property: {properties.find((p: any) => (p.uuid || String(p.id)) === selectedPropertyId)?.name || '—'}</span>}
        </div>
      )}

      {/* ═══ METHOD SELECTION ═══ */}
      {step === 'method' && (
        <div className="space-y-4">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Property</label>
            <select className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" value={selectedPropertyId} onChange={e => setSelectedPropertyId(e.target.value)}>
              <option value="">Select a property...</option>
              {properties.map((p: any) => <option key={p.id} value={p.uuid || String(p.id)}>{p.name}</option>)}
            </select>
          </div>
          <p className="text-sm text-gray-500 mb-4">How would you like to add cameras?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => setStep('discover')} disabled={!selectedPropertyId} className="p-5 rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all text-left disabled:opacity-40" >
              <div className="flex items-center gap-3 mb-2"><span className="text-2xl">🔍</span><h3 className="font-semibold text-gray-900">Auto-Discover</h3></div>
              <p className="text-xs text-gray-500">Scan your network for ONVIF cameras. Works with most IP cameras automatically.</p>
              <span className="mt-2 inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Recommended</span>
            </button>
            <button onClick={() => setStep('manual')} disabled={!selectedPropertyId} className="p-5 rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all text-left disabled:opacity-40">
              <div className="flex items-center gap-3 mb-2"><span className="text-2xl">📝</span><h3 className="font-semibold text-gray-900">Manual RTSP</h3></div>
              <p className="text-xs text-gray-500">Enter an RTSP stream URL directly. Works with any camera that supports RTSP.</p>
            </button>
            <button onClick={() => setStep('nvr')} disabled={!selectedPropertyId} className="p-5 rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all text-left disabled:opacity-40">
              <div className="flex items-center gap-3 mb-2"><span className="text-2xl">🖥️</span><h3 className="font-semibold text-gray-900">Connect NVR</h3></div>
              <p className="text-xs text-gray-500">Connect to a Hikvision or Dahua NVR and import all channels at once.</p>
            </button>
            <button onClick={() => setStep('bridge')} disabled={!selectedPropertyId} className="p-5 rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all text-left disabled:opacity-40">
              <div className="flex items-center gap-3 mb-2"><span className="text-2xl">📷</span><h3 className="font-semibold text-gray-900">Bridge Camera</h3></div>
              <p className="text-xs text-gray-500">Add an Intrlock Bridge Camera (ESP32-CAM) already on your network.</p>
            </button>
            {onTuringChosen && (
              <button
                onClick={() => { onClose(); onTuringChosen(); }}
                className="p-5 rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all text-left"
              >
                <div className="flex items-center gap-3 mb-2"><span className="text-2xl">☁️</span><h3 className="font-semibold text-gray-900">Turing Vision</h3></div>
                <p className="text-xs text-gray-500">Connect a Turing Vision cloud account. Paste an access token and imports all cameras + speakers.</p>
                <span className="mt-2 inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">Cloud</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══ AUTO-DISCOVER ═══ */}
      {step === 'discover' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between"><h3 className="font-semibold text-gray-900">Auto-Discover Cameras</h3><Button size="sm" onClick={handleDiscover} disabled={discovering}>{discovering ? 'Scanning...' : 'Scan Network'}</Button></div>
          {discovering && <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-3" /><p className="text-sm text-gray-500">Scanning for ONVIF cameras...</p></div>}
          {!discovering && discoveredCameras.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <span className="text-4xl block mb-3">📡</span>
              <p className="text-sm text-gray-500 mb-2">No cameras discovered yet</p>
              <p className="text-xs text-gray-400 mb-4">Make sure cameras are on the same network as an Intrlock Bridge.</p>
              <div className="flex gap-2 justify-center"><Button size="sm" onClick={handleDiscover}>Scan Again</Button><Button size="sm" variant="secondary" onClick={() => setStep('manual')}>Enter Manually</Button></div>
            </div>
          )}
          {discoveredCameras.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-3">Found {discoveredCameras.length} camera(s):</p>
              <div className="space-y-2 mb-4">
                {discoveredCameras.map((cam, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                    <input type="checkbox" checked={cam.selected} onChange={() => { const u = [...discoveredCameras]; u[i] = { ...cam, selected: !cam.selected }; setDiscoveredCameras(u); }} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
                    <div className="flex-1"><p className="text-sm font-medium text-gray-900">{cam.name}</p><p className="text-xs text-gray-400">{cam.ip} — {cam.manufacturer} {cam.model}</p></div>
                    <Badge color="green">Online</Badge>
                  </div>
                ))}
              </div>
              <Button onClick={addMultiple} disabled={saving || !discoveredCameras.some(c => c.selected)}>{saving ? 'Adding...' : `Add ${discoveredCameras.filter(c => c.selected).length} Camera(s)`}</Button>
            </div>
          )}
        </div>
      )}

      {/* ═══ MANUAL RTSP ═══ */}
      {step === 'manual' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 mb-4">Add Camera via RTSP</h3>
          <Input label="Camera Name" placeholder="e.g., Front Door Camera" value={manualForm.name} onChange={(e: any) => setManualForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="RTSP URL" placeholder="rtsp://192.168.1.100:554/stream1" value={manualForm.rtsp_url} onChange={(e: any) => setManualForm(f => ({ ...f, rtsp_url: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Username (optional)" placeholder="admin" value={manualForm.username} onChange={(e: any) => setManualForm(f => ({ ...f, username: e.target.value }))} />
            <Input label="Password (optional)" type="password" value={manualForm.password} onChange={(e: any) => setManualForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-xs text-emerald-800">
            <p className="font-semibold mb-1">Common RTSP URL formats:</p>
            <ul className="space-y-1 text-emerald-700">
              <li>Hikvision: <code className="bg-emerald-100 px-1 rounded">rtsp://user:pass@IP:554/Streaming/Channels/101</code></li>
              <li>Dahua: <code className="bg-emerald-100 px-1 rounded">rtsp://user:pass@IP:554/cam/realmonitor?channel=1&subtype=0</code></li>
              <li>Generic: <code className="bg-emerald-100 px-1 rounded">rtsp://user:pass@IP:554/stream1</code></li>
            </ul>
          </div>
          <Button onClick={() => {
            let url = manualForm.rtsp_url;
            if (manualForm.username && !url.includes('@')) url = url.replace('rtsp://', `rtsp://${manualForm.username}:${manualForm.password}@`);
            addCamera({ name: manualForm.name, rtsp_url: url, source: 'rtsp_manual' });
          }} disabled={saving || !manualForm.name || !manualForm.rtsp_url}>{saving ? 'Adding...' : 'Add Camera'}</Button>
        </div>
      )}

      {/* ═══ NVR ═══ */}
      {step === 'nvr' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 mb-4">Connect to NVR</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="NVR IP Address" placeholder="192.168.1.64" value={nvrForm.host} onChange={(e: any) => setNvrForm(f => ({ ...f, host: e.target.value }))} />
            <Input label="Port" placeholder="80" value={nvrForm.port} onChange={(e: any) => setNvrForm(f => ({ ...f, port: e.target.value }))} />
            <Input label="Username" placeholder="admin" value={nvrForm.username} onChange={(e: any) => setNvrForm(f => ({ ...f, username: e.target.value }))} />
            <Input label="Password" type="password" value={nvrForm.password} onChange={(e: any) => setNvrForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">NVR Brand</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={nvrForm.type} onChange={e => setNvrForm(f => ({ ...f, type: e.target.value }))}>
              <option value="hikvision">Hikvision</option><option value="dahua">Dahua</option>
            </select>
          </div>
          <Button onClick={handleNvrConnect} disabled={nvrLoading || !nvrForm.host || !nvrForm.password}>{nvrLoading ? 'Connecting...' : 'Connect to NVR'}</Button>
          {nvrCameras.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-3">Found {nvrCameras.length} channel(s):</p>
              <div className="space-y-2">{nvrCameras.map((cam: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                  <div><p className="text-sm font-medium text-gray-900">{cam.name}</p><p className="text-xs text-gray-400 font-mono">{cam.rtsp_url}</p></div>
                  <Button size="sm" onClick={() => addCamera({ name: cam.name, rtsp_url: cam.rtsp_url, source: 'nvr' })}>Add</Button>
                </div>
              ))}</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ BRIDGE CAMERA ═══ */}
      {step === 'bridge' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 mb-4">Add Bridge Camera</h3>
          {bridgeDevices.length > 0 ? (
            <div className="space-y-2 mb-4">{bridgeDevices.map((dev: any) => (
              <button key={dev.id} onClick={() => setBridgeForm({ device_id: dev.serial_number || dev.id, name: dev.name || `Bridge Cam ${dev.id}` })}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${bridgeForm.device_id === (dev.serial_number || dev.id) ? 'border-brand-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="text-sm font-medium text-gray-900">{dev.name || 'Bridge Camera'}</p>
                <p className="text-xs text-gray-400">{dev.local_ip || dev.ip_address || 'IP unknown'} — {dev.status || 'unknown'}</p>
              </button>
            ))}</div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center"><p className="text-sm text-gray-500">No Bridge Cameras detected</p><p className="text-xs text-gray-400 mt-1">Make sure your Bridge Camera is powered on and connected to WiFi</p></div>
          )}
          <Input label="Camera Name" placeholder="e.g., Lobby Camera" value={bridgeForm.name} onChange={(e: any) => setBridgeForm(f => ({ ...f, name: e.target.value }))} />
          <Button onClick={() => addCamera({ name: bridgeForm.name, rtsp_url: '', source: 'bridge' })} disabled={saving || !bridgeForm.name}>{saving ? 'Adding...' : 'Add Bridge Camera'}</Button>
        </div>
      )}

      {/* ═══ DONE ═══ */}
      {step === 'done' && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h3 className="font-semibold text-gray-900 text-lg mb-2">Camera Added!</h3>
          <p className="text-sm text-gray-500 mb-6">Your camera is now connected and will appear in the dashboard.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => setStep('method')}>Add Another</Button>
            <Button variant="secondary" onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
